import Link from "next/link";

import type { PromptInsights } from "@/lib/api/types";
import { formatCount } from "@/lib/format";
import { FUNNEL_LABEL, formatShare } from "@/lib/prompt-module";

export type InsightsStatus = "all" | "freigegeben";

type Props = {
  /** `null` solange die Auswertung lädt. */
  insights: PromptInsights | null;
  error: string | null;
  status: InsightsStatus;
  onStatus: (status: InsightsStatus) => void;
  /** Cluster-Zeile: Briefing zu diesem Cluster starten. */
  onBriefing: (tag: string) => void;
  onClose: () => void;
};

const TH = "px-3 py-2 font-medium";
const TD = "px-3 py-2.5 text-[13px]";

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex min-w-[170px] flex-1 flex-col gap-0.5 rounded-md border border-neutral-bg px-4 py-3">
      <span className="text-xs text-muted">{label}</span>
      <span className="font-mono text-xl font-semibold">{value}</span>
      {hint && <span className="text-xs text-muted">{hint}</span>}
    </div>
  );
}

function Block({ id, title, hint, children }: { id: string; title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section aria-labelledby={id} className="flex flex-col gap-2">
      <div className="flex flex-col gap-0.5">
        <h3 id={id} className="text-sm font-semibold">
          {title}
        </h3>
        {hint && <span className="text-xs text-muted">{hint}</span>}
      </div>
      {children}
    </section>
  );
}

/**
 * Auswertung der gespeicherten Trigger-Tests eines Topics (SPEC 4.3): Wie oft die eigene Domain zitiert und die Marke
 * genannt wird, wer sonst zitiert wird und wo Lücken sind. Kostenlos, es werden nur gespeicherte Läufe ausgewertet.
 */
export function InsightsPanel({ insights, error, status, onStatus, onBriefing, onClose }: Props) {
  return (
    <section aria-labelledby="insights-title" className="flex flex-col gap-5 rounded-lg border border-line bg-white px-5 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <h2 id="insights-title" className="text-base font-semibold">
            Auswertung der Trigger-Tests
          </h2>
          <span className="text-xs text-muted">Zitierte Quellen, Wettbewerber und Lücken aus den gespeicherten Läufen. Kostenlos.</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div role="group" aria-label="Prompts der Auswertung" className="flex gap-1.5">
            {(
              [
                ["all", "Alle Prompts"],
                ["freigegeben", "Nur freigegebene"],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                aria-pressed={status === key}
                onClick={() => onStatus(key)}
                className={`h-11 rounded-full border px-3.5 text-[13px] ${status === key ? "border-ink bg-ink text-white" : "border-field bg-white"}`}
              >
                {label}
              </button>
            ))}
          </div>
          <button type="button" onClick={onClose} className="h-11 rounded-md border border-field bg-white px-3.5 text-sm">
            Schließen
          </button>
        </div>
      </div>

      {error && (
        <p role="alert" className="text-[13px] text-danger">
          Die Auswertung konnte nicht geladen werden: {error}
        </p>
      )}
      {insights === null && !error && <p className="text-sm text-muted">Auswertung wird geladen …</p>}

      {insights !== null && insights.prompts_tested === 0 && (
        <p className="text-sm text-muted">
          Noch kein Trigger-Test{status === "freigegeben" ? " für freigegebene Prompts" : ""}. Starte einen Trigger-Test, dann erscheint hier die Auswertung.
        </p>
      )}

      {insights !== null && insights.prompts_tested > 0 && <InsightsBody insights={insights} onBriefing={onBriefing} />}
    </section>
  );
}

function InsightsBody({ insights, onBriefing }: { insights: PromptInsights; onBriefing: (tag: string) => void }) {
  const ownRank = insights.own_rank;
  return (
    <>
      {insights.legacy && (
        <p role="status" className="rounded-md bg-warning-bg px-3 py-2 text-[13px] text-warning">
          Hinweis: Ein Teil der Ergebnisse stammt aus älteren Trigger-Tests. Dort fehlen Zahlen je Domain und Marke, die Werte sind darum
          Näherungen. Ein neuer Trigger-Test liefert die genauen Zahlen.
        </p>
      )}

      <div className="flex flex-wrap gap-3">
        <Kpi
          label="Eigene Domain zitiert"
          value={formatShare(insights.cited_share)}
          hint={`${formatCount(insights.cited_runs)} von ${formatCount(insights.runs)} Läufen · ${insights.own_domain}`}
        />
        <Kpi
          label="Marke genannt"
          value={formatShare(insights.brand_share)}
          hint={`${formatCount(insights.brand_runs)} von ${formatCount(insights.answers)} Antworten · ${insights.brand}`}
        />
        <Kpi
          label="Prompts getestet"
          value={`${formatCount(insights.prompts_tested)} / ${formatCount(insights.prompts_total)}`}
          hint={`${formatCount(insights.web_runs)} Läufe mit Websuche`}
        />
        <Kpi
          label="Rang der eigenen Domain"
          value={ownRank === null ? "–" : `${formatCount(ownRank)}.`}
          hint={ownRank === null ? "unter den zitierten Quellen nicht vorhanden" : "unter den zitierten Quellen"}
        />
      </div>

      <Block id="insights-platforms" title="Plattformen">
        <div className="overflow-x-auto">
          <table aria-label="Plattformen" className="w-full min-w-[560px] border-collapse text-sm">
            <thead>
              <tr className="text-left text-xs text-muted">
                <th className={TH}>Plattform</th>
                <th className={`${TH} text-right`}>Läufe</th>
                <th className={`${TH} text-right`}>Mit Websuche</th>
                <th className={`${TH} text-right`}>Domain zitiert</th>
                <th className={`${TH} text-right`}>Marke genannt</th>
              </tr>
            </thead>
            <tbody>
              {insights.platforms.map((platform) => (
                <tr key={platform.key} className="border-t border-neutral-bg">
                  <td className={TD}>{platform.label}</td>
                  <td className={`${TD} text-right font-mono`}>{formatCount(platform.runs)}</td>
                  <td className={`${TD} text-right font-mono`}>{formatCount(platform.web)}</td>
                  <td className={`${TD} text-right font-mono`}>{formatShare(platform.cited_share)}</td>
                  <td className={`${TD} text-right font-mono`}>{formatShare(platform.brand_share)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Block>

      {insights.groups.length > 0 && (
        <Block id="insights-groups" title="Cluster und Funnel" hint="Wo die eigene Seite zitiert wird. Aus einem Cluster lässt sich ein Briefing starten.">
          <div className="overflow-x-auto">
            <table aria-label="Cluster und Funnel" className="w-full min-w-[640px] border-collapse text-sm">
              <thead>
                <tr className="text-left text-xs text-muted">
                  <th className={TH}>Cluster</th>
                  <th className={TH}>Funnel</th>
                  <th className={`${TH} text-right`}>Prompts</th>
                  <th className={`${TH} text-right`}>Domain zitiert</th>
                  <th className={`${TH} text-right`}>Marke genannt</th>
                  <th className={TH} />
                </tr>
              </thead>
              <tbody>
                {insights.groups.map((group) => (
                  <tr key={`${group.tag ?? ""}|${group.funnel ?? ""}`} className="border-t border-neutral-bg">
                    <td className={TD}>{group.tag ?? "Ohne Cluster"}</td>
                    <td className={TD}>{group.funnel ? FUNNEL_LABEL[group.funnel] : "–"}</td>
                    <td className={`${TD} text-right font-mono`}>{formatCount(group.prompts)}</td>
                    <td className={`${TD} text-right font-mono`}>{formatShare(group.cited_share)}</td>
                    <td className={`${TD} text-right font-mono`}>{formatShare(group.brand_share)}</td>
                    <td className="px-3 py-1.5 text-right">
                      {group.tag && (
                        <button
                          type="button"
                          onClick={() => onBriefing(group.tag as string)}
                          aria-label={`Briefing zum Cluster ${group.tag} starten`}
                          className="h-11 rounded-md border border-field bg-white px-3 text-[13px]"
                        >
                          Briefing starten
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Block>
      )}

      <Block id="insights-domains" title="Zitierte Quellen" hint="Domains, die in den Antworten mit Websuche zitiert werden. Die eigene Domain ist markiert.">
        {insights.domains.length === 0 ? (
          <p className="text-sm text-muted">Keine zitierten Quellen in den gespeicherten Läufen.</p>
        ) : (
          <div className="overflow-x-auto">
            <table aria-label="Zitierte Quellen" className="w-full min-w-[560px] border-collapse text-sm">
              <thead>
                <tr className="text-left text-xs text-muted">
                  <th className={TH}>Domain</th>
                  <th className={`${TH} text-right`}>Zitate</th>
                  <th className={`${TH} text-right`}>Prompts</th>
                  <th className={TH}>Plattformen</th>
                </tr>
              </thead>
              <tbody>
                {insights.domains.map((domain) => (
                  <tr key={domain.domain} className={`border-t border-neutral-bg ${domain.own ? "bg-[#eef3fa]" : ""}`}>
                    <td className={TD}>
                      {domain.domain}
                      {domain.own && <span className="ml-2 rounded-full bg-info-bg px-2 py-0.5 text-xs text-info">Eigene Domain</span>}
                    </td>
                    <td className={`${TD} text-right font-mono`}>{formatCount(domain.runs)}</td>
                    <td className={`${TD} text-right font-mono`}>{formatCount(domain.prompts)}</td>
                    <td className={TD}>{domain.platforms.join(", ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Block>

      <Block
        id="insights-competitors"
        title="Genannte Marken"
        hint="Marken, die in den Antworten genannt werden. Anteil = Prompts, bei denen die Marke vorkommt."
      >
        {insights.competitors.length === 0 ? (
          <p className="text-sm text-muted">Keine fremden Marken erkannt.</p>
        ) : (
          <div className="overflow-x-auto">
            <table aria-label="Genannte Marken" className="w-full min-w-[480px] border-collapse text-sm">
              <thead>
                <tr className="text-left text-xs text-muted">
                  <th className={TH}>Marke</th>
                  <th className={`${TH} text-right`}>Nennungen</th>
                  <th className={`${TH} text-right`}>Prompts</th>
                  <th className={`${TH} text-right`}>Anteil</th>
                </tr>
              </thead>
              <tbody>
                {insights.competitors.map((brand) => (
                  <tr key={brand.name} className="border-t border-neutral-bg">
                    <td className={TD}>{brand.name}</td>
                    <td className={`${TD} text-right font-mono`}>{formatCount(brand.runs)}</td>
                    <td className={`${TD} text-right font-mono`}>{formatCount(brand.prompts)}</td>
                    <td className={`${TD} text-right font-mono`}>{formatShare(brand.share)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Block>

      <Block
        id="insights-gaps"
        title={`Lücken (${formatCount(insights.gap_count)})`}
        hint="Prompts, bei denen die Antworten die Websuche nutzen und andere Quellen oder Marken nennen, die eigene Seite aber nicht zitiert wird."
      >
        {insights.gaps.length === 0 ? (
          <p className="text-sm text-muted">Keine Lücken gefunden.</p>
        ) : (
          <div className="overflow-x-auto">
            <table aria-label="Lücken" className="w-full min-w-[720px] border-collapse text-sm">
              <thead>
                <tr className="text-left text-xs text-muted">
                  <th className={TH}>Prompt</th>
                  <th className={TH}>Cluster</th>
                  <th className={TH}>Genannte Marken</th>
                  <th className={TH}>Zitierte Quellen</th>
                </tr>
              </thead>
              <tbody>
                {insights.gaps.map((gap) => (
                  <tr key={gap.id} className="border-t border-neutral-bg align-top">
                    <td className={TD}>{gap.text}</td>
                    <td className={TD}>{gap.tag ?? "–"}</td>
                    <td className={TD}>{gap.competitors.join(", ") || "–"}</td>
                    <td className={TD}>{gap.sources.join(", ") || "–"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Block>

      <Block id="insights-own" title="Eigene zitierte Seiten" hint="Seiten der eigenen Domain, die in Antworten zitiert wurden. Die Analyse prüft, was sie stark macht.">
        {insights.own_urls.length === 0 ? (
          <p className="text-sm text-muted">Die eigene Domain wurde in den gespeicherten Läufen nicht mit einer Seite zitiert.</p>
        ) : (
          <div className="overflow-x-auto">
            <table aria-label="Eigene zitierte Seiten" className="w-full min-w-[640px] border-collapse text-sm">
              <thead>
                <tr className="text-left text-xs text-muted">
                  <th className={TH}>Seite</th>
                  <th className={`${TH} text-right`}>Zitate</th>
                  <th className={TH}>Prompts</th>
                  <th className={TH} />
                </tr>
              </thead>
              <tbody>
                {insights.own_urls.map((page) => (
                  <tr key={page.url} className="border-t border-neutral-bg align-top">
                    <td className={`${TD} break-all`}>{page.url}</td>
                    <td className={`${TD} text-right font-mono`}>{formatCount(page.runs)}</td>
                    <td className={TD}>{page.prompts.map((prompt) => prompt.text).join(" · ")}</td>
                    <td className="px-3 py-1.5 text-right">
                      <Link
                        href={`/onpage/neu?url=${encodeURIComponent(page.url)}`}
                        aria-label={`Seite ${page.url} analysieren`}
                        className="inline-flex h-11 items-center rounded-md border border-field bg-white px-3 text-[13px]"
                      >
                        Seite analysieren
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Block>
    </>
  );
}
